import { useCallback, useEffect, useRef, useState } from "react";
import { MdSend, MdCheckCircle, MdRefresh, MdMail, MdPhone, MdChatBubbleOutline } from "react-icons/md";
import Card from "components/card";
import { api } from "api";

type Status = "open" | "answered" | "closed";

type ThreadSummary = {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  status: Status;
  admin_unread_count: number;
  user_unread_count: number;
  last_message_at: string;
  last_message_preview: string;
  last_message_sender: "user" | "admin" | null;
};

type Message = {
  id: number;
  sender_type: "user" | "admin";
  body: string;
  category?: string | null;
  app_version?: string | null;
  platform?: string | null;
  created_at: string;
};

type ThreadDetail = {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  user_phone?: string | null;
  status: Status;
  messages: Message[];
};

const CAT_LABELS: Record<string, { label: string; color: string }> = {
  bug: { label: "Hata", color: "#dc2626" },
  suggestion: { label: "Öneri", color: "#f59e0b" },
  request: { label: "Talep", color: "#8b5cf6" },
  question: { label: "Soru", color: "#3b82f6" },
  general: { label: "Genel", color: "#64748b" },
};

const Destek = () => {
  const [threads, setThreads] = useState<ThreadSummary[]>([]);
  const [filter, setFilter] = useState<"all" | "open" | "answered" | "closed">("all");
  const [selected, setSelected] = useState<ThreadDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, 50);
  };

  const loadThreads = useCallback(async () => {
    setLoading(true);
    try {
      const status = filter === "all" ? undefined : filter;
      const list: any = await api.feedbackThreads(status);
      setThreads(list || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  const openThread = async (threadId: number) => {
    setLoadingDetail(true);
    try {
      const detail: any = await api.feedbackDetail(threadId);
      setSelected(detail);
      scrollToBottom();
      // Reload threads to reset admin_unread_count
      loadThreads();
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleReply = async () => {
    if (!selected || !reply.trim() || sending) return;
    setSending(true);
    try {
      const res: any = await api.feedbackReply(selected.id, reply.trim());
      if (res?.data) {
        setSelected({ ...selected, status: "answered", messages: [...selected.messages, res.data] });
      }
      setReply("");
      scrollToBottom();
      loadThreads();
    } catch (e: any) {
      alert(e?.message || "Cevap gönderilemedi");
    } finally {
      setSending(false);
    }
  };

  const handleClose = async () => {
    if (!selected) return;
    if (!window.confirm("Bu konuşmayı kapatmak istediğinize emin misiniz?")) return;
    try {
      await api.feedbackClose(selected.id);
      setSelected({ ...selected, status: "closed" });
      loadThreads();
    } catch (e: any) { alert(e?.message || "Kapatılamadı"); }
  };

  const handleReopen = async () => {
    if (!selected) return;
    try {
      await api.feedbackReopen(selected.id);
      setSelected({ ...selected, status: "open" });
      loadThreads();
    } catch (e: any) { alert(e?.message || "Yeniden açılamadı"); }
  };

  const fmtTime = (iso: string) => {
    try {
      const d = new Date(iso);
      const now = new Date();
      const sameDay = d.toDateString() === now.toDateString();
      if (sameDay) return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
      return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "2-digit" }) + " " +
        d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };

  const statusBadge = (s: Status) => {
    const map: Record<Status, { label: string; color: string }> = {
      open: { label: "Bekliyor", color: "bg-orange-100 text-orange-700" },
      answered: { label: "Cevaplandı", color: "bg-green-100 text-green-700" },
      closed: { label: "Kapalı", color: "bg-gray-100 text-gray-600" },
    };
    const x = map[s];
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${x.color}`}>{x.label}</span>;
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h4 className="text-lg font-bold text-navy-700 dark:text-white">Destek Talepleri</h4>
          <p className="text-sm text-gray-500">Kullanıcı geri bildirimleri, hata ve öneri sohbetleri</p>
        </div>
        <button onClick={loadThreads} className="flex items-center gap-1 text-sm text-brand-500 hover:text-brand-600">
          <MdRefresh /> Yenile
        </button>
      </div>

      {/* Filtre tabs */}
      <div className="mb-4 flex gap-2 flex-wrap">
        {([["all", "Tümü"], ["open", "Bekleyen"], ["answered", "Cevaplandı"], ["closed", "Kapalı"]] as const).map(([k, l]) => (
          <button
            key={k}
            onClick={() => setFilter(k as any)}
            className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
              filter === k ? "bg-brand-500 text-white" : "bg-white text-gray-700 hover:bg-gray-100 dark:bg-navy-700 dark:text-white"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" style={{ height: "calc(100vh - 280px)", minHeight: 500 }}>
        {/* Sol: Thread listesi */}
        <Card extra="overflow-hidden flex flex-col">
          <div className="p-3 border-b border-gray-200 dark:border-navy-600 font-semibold text-sm text-navy-700 dark:text-white">
            Konuşmalar ({threads.length})
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center text-gray-400 text-sm">Yükleniyor...</div>
            ) : threads.length === 0 ? (
              <div className="p-6 text-center text-gray-400 text-sm">
                <MdChatBubbleOutline className="mx-auto text-gray-300" size={40} />
                <p className="mt-2">Henüz mesaj yok</p>
              </div>
            ) : (
              threads.map(t => (
                <div
                  key={t.id}
                  onClick={() => openThread(t.id)}
                  className={`p-3 border-b border-gray-100 dark:border-navy-700 cursor-pointer transition ${
                    selected?.id === t.id ? "bg-brand-50 dark:bg-navy-700" : "hover:bg-gray-50 dark:hover:bg-navy-700"
                  }`}
                >
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm text-navy-700 dark:text-white truncate">{t.user_name}</p>
                        {t.admin_unread_count > 0 && (
                          <span className="bg-brand-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{t.admin_unread_count}</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">{t.user_email}</p>
                    </div>
                    {statusBadge(t.status)}
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-300 mt-1.5 line-clamp-2">
                    {t.last_message_sender === "admin" ? "Siz: " : ""}{t.last_message_preview}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-1">{fmtTime(t.last_message_at)}</p>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Sağ: Konuşma detayı */}
        <Card extra="overflow-hidden flex flex-col lg:col-span-2">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-center px-6">
              <div>
                <MdChatBubbleOutline className="mx-auto text-gray-300" size={56} />
                <p className="mt-3 text-sm">Sol listeden bir konuşma seçin</p>
              </div>
            </div>
          ) : loadingDetail ? (
            <div className="flex-1 flex items-center justify-center text-gray-400">Yükleniyor...</div>
          ) : (
            <>
              {/* Detay header */}
              <div className="p-3 border-b border-gray-200 dark:border-navy-600 bg-gray-50 dark:bg-navy-700">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-bold text-navy-700 dark:text-white">{selected.user_name}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                      <span className="flex items-center gap-1"><MdMail size={14} />{selected.user_email}</span>
                      {selected.user_phone && <span className="flex items-center gap-1"><MdPhone size={14} />{selected.user_phone}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {statusBadge(selected.status)}
                    {selected.status !== "closed" ? (
                      <button onClick={handleClose} className="flex items-center gap-1 text-xs text-gray-600 hover:text-red-600 px-2 py-1 rounded">
                        <MdCheckCircle size={14} /> Kapat
                      </button>
                    ) : (
                      <button onClick={handleReopen} className="flex items-center gap-1 text-xs text-brand-500 hover:text-brand-600 px-2 py-1 rounded">
                        <MdRefresh size={14} /> Yeniden Aç
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Mesajlar */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-navy-900 space-y-3">
                {selected.messages.map(m => {
                  const isAdmin = m.sender_type === "admin";
                  const cat = m.category ? CAT_LABELS[m.category] : null;
                  return (
                    <div key={m.id} className={`flex ${isAdmin ? "justify-end" : "justify-start"}`}>
                      <div
                        className={`max-w-[80%] rounded-2xl px-4 py-2.5 shadow-sm ${
                          isAdmin
                            ? "bg-brand-500 text-white rounded-br-sm"
                            : "bg-white dark:bg-navy-700 text-navy-700 dark:text-white border border-gray-200 dark:border-navy-600 rounded-bl-sm"
                        }`}
                      >
                        {!isAdmin && cat && (
                          <div className="text-[10px] font-bold mb-1 tracking-wider" style={{ color: cat.color }}>
                            {cat.label.toUpperCase()}
                            {m.platform && ` · ${m.platform.toUpperCase()}`}
                            {m.app_version && ` · v${m.app_version}`}
                          </div>
                        )}
                        {isAdmin && (
                          <div className="text-[10px] font-bold opacity-80 mb-1 tracking-wider">SİZ</div>
                        )}
                        <div className="text-sm leading-relaxed whitespace-pre-wrap">{m.body}</div>
                        <div className={`text-[10px] mt-1 text-right ${isAdmin ? "text-white/70" : "text-gray-400"}`}>
                          {fmtTime(m.created_at)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Reply composer */}
              {selected.status !== "closed" && (
                <div className="border-t border-gray-200 dark:border-navy-600 bg-white dark:bg-navy-800 p-3">
                  <div className="flex items-end gap-2">
                    <textarea
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleReply(); } }}
                      placeholder="Cevabınızı yazın... (Enter = gönder, Shift+Enter = yeni satır)"
                      rows={2}
                      maxLength={5000}
                      className="flex-1 resize-none rounded-xl border border-gray-200 dark:border-navy-600 bg-gray-50 dark:bg-navy-700 text-navy-700 dark:text-white p-3 text-sm outline-none focus:border-brand-500"
                    />
                    <button
                      onClick={handleReply}
                      disabled={sending || !reply.trim()}
                      className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-500 text-white shadow hover:bg-brand-600 disabled:opacity-40"
                    >
                      <MdSend size={20} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Destek;
